import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  ArrowLeft, Edit2, CheckCircle, XCircle, Ban, Send,
  Plane, Hotel, Car, ParkingSquare, ArrowLeftRight, UtensilsCrossed, MoreHorizontal,
  Lock, Clock, Trash2, Receipt, Star, AlertTriangle, Calendar, Copy, CheckSquare,
  ChevronDown, ChevronUp, Shield, Search, RefreshCw, RotateCcw,
} from 'lucide-react';
import { doc, getDoc, getDocs, updateDoc, arrayRemove, arrayUnion, collection, addDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import Modal from '../shared/Modal';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { useApprovalScope, matchesApprovalScope } from '../../hooks/useApprovalScope';
import { StatusBadge, getDisplayStatus, leadTimeDays, LeadTimeBadge, STATUS_CONFIG } from './TripList';
import Attachments from './Attachments';
import TripRatingModal from './TripRatingModal';
import TravellerAccessibilityCard from './TravellerAccessibilityCard';
import { generateICS } from '../../utils/formatters';
import ProposedHotelCard from '../hotels/ProposedHotelCard';
import {
  tripRequiresApproval, hotelFlowMode, hasPendingOption,
  sectorPatchFromOption, sectorPatchFromBooking, encodeSavedOption,
} from '../../utils/hotelBooking';
import { sectorReportingCity, tripCreditInclGST, tripCreditExGST } from '../../utils/reportHelpers';
import {
  flightFlowMode, hasPendingFlightOption, encodeSavedFlight,
  sectorPatchFromFlightOption, sectorPatchFromFlightBooking,
  flightPassengersFor, flightPassengerParam, newBookingPassengers, sectorExtras,
} from '../../utils/flightBooking';
import { resolveTravellerProfile } from '../../utils/passengerNames';
import { buildBookingUrl, BOOKING_URL_KEYS } from '../../utils/bookingContext';
import ProposedFlightCard from '../flights/ProposedFlightCard';
import FlightCancelModal from '../flights/FlightCancelModal';
import { ssrRequirements, needsAdvanceNotice, hasUnarrangedSsr } from '../../data/ssrCodes';

const SECTOR_ICONS = {
  flight:        Plane,
  accommodation: Hotel,
  'car-hire':    Car,
  parking:       ParkingSquare,
  transfers:     ArrowLeftRight,
  meals:         UtensilsCrossed,
  other:         MoreHorizontal,
};

const SECTOR_LABELS = {
  flight:        'Flight',
  accommodation: 'Accommodation',
  'car-hire':    'Car Hire',
  parking:       'Parking',
  transfers:     'Transfers',
  meals:         'Meals',
  other:         'Other',
};

const ACCOM_ACCESS_LABELS = {
  roll_in_shower:     'Roll-in shower',
  grab_rails:         'Grab rails / bathroom support',
  bath_hoist:         'Bath hoist / shower chair',
  ground_floor:       'Ground floor or lift access required',
  wide_doorways:      'Wide doorways / turning circle (≥ 900mm)',
  hearing_loop:       'Hearing loop / visual fire alarm',
  accessible_parking: 'Accessible parking at property',
  carer_bed:          'Carer / attendant bed in same room',
  adjustable_bed:     'Height-adjustable or profiling bed',
  no_steps:           'No steps at entry / throughout property',
};

function generateChecklist(trip, passenger) {
  const items = [];
  items.push({ key: 'itinerary_sent',    label: 'Digital itinerary sent to traveller', checked: false, waived: false });
  items.push({ key: 'contact_confirmed', label: 'Traveller contact details confirmed', checked: false, waived: false });

  if (passenger?.mobilityAids?.length || passenger?.wheelchairModel) {
    items.push({ key: 'mobility_aid_airline',  label: 'Mobility aid notified to airline — dimensions and battery type confirmed', checked: false, waived: false });
    items.push({ key: 'mobility_aid_handling', label: 'Ground handling instructions confirmed with airline', checked: false, waived: false });
  }

  const hasWheelchairSSR = (trip.sectors || []).some(s =>
    s.type === 'flight' && (s.specialAssistance || []).some(c => ['WCHR', 'WCHP', 'WCHC'].includes(c))
  );
  if (hasWheelchairSSR) {
    items.push({ key: 'wheelchair_assist_confirmed', label: 'Airport wheelchair assistance confirmed at origin and destination', checked: false, waived: false });
  }

  (trip.sectors || []).filter(s => s.type === 'accommodation' && (s.accessibilityRequirements || []).length > 0).forEach((s, i) => {
    items.push({ key: `accom_access_${i}`, label: `Accessible room requirements confirmed with ${s.propertyName || 'accommodation provider'}`, checked: false, waived: false });
  });

  const hasSupportWorker = (trip.additionalPassengers || []).some(p => ['support_worker', 'carer'].includes(p.role));
  if (hasSupportWorker || passenger?.carerRequired) {
    items.push({ key: 'carer_seating', label: 'Carer / support worker seated adjacent to traveller on all flights', checked: false, waived: false });
    items.push({ key: 'carer_rooming', label: 'Carer / support worker room booked adjacent or in same room', checked: false, waived: false });
  }

  const hasDPNA = (trip.sectors || []).some(s => s.type === 'flight' && (s.specialAssistance || []).includes('DPNA'));
  if (hasDPNA) {
    items.push({ key: 'dpna_briefed', label: 'Airline briefed on traveller support needs — ground and cabin crew notified', checked: false, waived: false });
  }

  const hasInternational = (trip.sectors || []).some(s => s.international);
  if (hasInternational) {
    items.push({ key: 'insurance_confirmed', label: 'Travel insurance confirmed and policy details recorded on trip', checked: false, waived: false });
    items.push({ key: 'visa_confirmed',      label: 'Visa / entry requirements checked for destination country', checked: false, waived: false });
    items.push({ key: 'emergency_contacts',  label: 'In-country emergency contacts confirmed', checked: false, waived: false });
  } else if (trip.travelInsurance && (trip.travelInsurance.provider || trip.travelInsurance.policyNumber)) {
    items.push({ key: 'insurance_confirmed', label: 'Travel insurance details confirmed and policy recorded', checked: false, waived: false });
  }
  return items;
}

function fmt(val) {
  return val || '—';
}

function fmtStatus(s) {
  return (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex gap-2">
      <span className="text-xs text-gray-600 w-32 shrink-0">{label}</span>
      <span className="text-xs text-gray-700">{value}</span>
    </div>
  );
}

function SectorCard({ sector, index, onSearchHotels, hotelMode, onSearchFlights, flightMode,
  flightBookingLocked, flightLockReason, onRecordSsr, canRecordSsr, onCancelFlight }) {
  const Icon = SECTOR_ICONS[sector.type] || MoreHorizontal;
  const label = SECTOR_LABELS[sector.type] || sector.type;

  const rows = [];

  if (sector.type === 'flight') {
    rows.push(
      <Row key="route" label="Route" value={[sector.from, sector.to].filter(Boolean).join(' → ') || null} />,
      <Row key="date" label="Date" value={sector.date} />,
      <Row key="dep" label="Departure" value={sector.departureTime} />,
      <Row key="arr" label="Arrival" value={sector.arrivalTime} />,
      <Row key="airline" label="Airline" value={sector.airline} />,
      <Row key="flt" label="Flight" value={sector.flightNumber} />,
      <Row key="ref" label="Booking ref" value={sector.bookingRef} />,
      <Row key="class" label="Class" value={sector.cabinClass} />,
    );
    // What was actually bought on this flight. Paid extras were recorded on the sector all along
    // but never shown, so a traveller's seat and bags were invisible once the window closed.
    const extras = sectorExtras(sector);
    if (extras.seats)   rows.push(<Row key="seats" label="Seats" value={extras.seats} />);
    if (extras.baggage) rows.push(<Row key="bags"  label="Bags purchased" value={extras.baggage} />);
    // Shown on the trip, not only in reconciliation: the first person to question this cost is
    // whoever opens the trip, and without it a converted fare that settled a few dollars out
    // reads as a typo in the portal.
    if (sector.priceConverted === true) {
      rows.push(
        <Row key="fx" label="Fare currency"
          value="Priced by the airline in a foreign currency — the cost shown is an exchange rate captured at the time of search" />
      );
    }
    if ((sector.specialAssistance || []).length > 0) {
      rows.push(
        <div key="ssr" className="flex gap-2 pt-1">
          <span className="text-xs text-gray-600 w-32 shrink-0">Special assist.</span>
          <div className="flex flex-wrap gap-1">
            {sector.specialAssistance.map(code => (
              <span key={code} className="px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-semibold">{code}</span>
            ))}
            {sector.specialAssistanceOther && <span className="text-xs text-gray-700 self-center">{sector.specialAssistanceOther}</span>}
          </div>
        </div>
      );
    }
  } else if (sector.type === 'accommodation') {
    const nights = sector.checkIn && sector.checkOut
      ? Math.max(0, Math.round((new Date(sector.checkOut) - new Date(sector.checkIn)) / 86400000))
      : null;
    rows.push(
      <Row key="name" label="Property" value={sector.propertyName} />,
      <Row key="in" label="Check-in" value={sector.checkIn} />,
      <Row key="out" label="Check-out" value={sector.checkOut} />,
      nights != null && <Row key="nights" label="Nights" value={String(nights)} />,
      <Row key="room" label="Room type" value={sector.roomType} />,
      <Row key="ref" label="Booking ref" value={sector.bookingRef} />,
      sectorReportingCity(sector) && <Row key="rcity" label="Reporting city" value={`${sectorReportingCity(sector)} (override)`} />,
    );
    if ((sector.accessibilityRequirements || []).length > 0 || sector.accessibilityNotes) {
      rows.push(
        <div key="access" className="flex gap-2 pt-1">
          <span className="text-xs text-gray-600 w-32 shrink-0">Accessibility</span>
          <div className="flex flex-wrap gap-1">
            {(sector.accessibilityRequirements || []).map(key => (
              <span key={key} className="px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-xs">{ACCOM_ACCESS_LABELS[key] || key}</span>
            ))}
            {sector.accessibilityNotes && <span className="text-xs text-gray-700 w-full mt-1">{sector.accessibilityNotes}</span>}
          </div>
        </div>
      );
    }
  } else if (sector.type === 'car-hire') {
    rows.push(
      <Row key="co" label="Company" value={sector.company} />,
      <Row key="pu" label="Pickup" value={`${sector.pickupLocation || ''}${sector.pickupDate ? ` · ${sector.pickupDate}` : ''}`} />,
      <Row key="do" label="Drop-off" value={`${sector.dropOffLocation || ''}${sector.dropOffDate ? ` · ${sector.dropOffDate}` : ''}`} />,
      <Row key="veh" label="Vehicle" value={sector.vehicleType} />,
      <Row key="ref" label="Booking ref" value={sector.bookingRef} />,
    );
  } else if (sector.type === 'parking') {
    rows.push(
      <Row key="fac" label="Facility" value={sector.facility} />,
      <Row key="entry" label="Entry" value={sector.entryDate} />,
      <Row key="exit" label="Exit" value={sector.exitDate} />,
      <Row key="ref" label="Booking ref" value={sector.bookingRef} />,
    );
  } else if (sector.type === 'transfers') {
    rows.push(
      <Row key="route" label="Route" value={[sector.from, sector.to].filter(Boolean).join(' → ') || null} />,
      <Row key="date" label="Date" value={sector.date} />,
      <Row key="time" label="Pickup time" value={sector.pickupTime} />,
      <Row key="type" label="Transfer type" value={sector.transferType} />,
      <Row key="prov" label="Provider" value={sector.provider} />,
    );
  } else if (sector.type === 'meals') {
    rows.push(
      <Row key="venue" label="Venue" value={sector.venue} />,
      <Row key="date" label="Date" value={sector.date} />,
      <Row key="meal" label="Meal type" value={sector.mealType} />,
      <Row key="pax" label="People" value={sector.numberOfPeople ? String(sector.numberOfPeople) : null} />,
    );
  } else {
    rows.push(
      <Row key="desc" label="Description" value={sector.description} />,
      <Row key="prov" label="Provider" value={sector.provider} />,
      <Row key="date" label="Date" value={sector.date} />,
    );
  }

  const cost = (() => {
    const c = parseFloat(sector.cost) || 0;
    if (!c) return null;
    return `A$${c.toFixed(2)}`;
  })();

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-200">
        <span className="text-xs text-gray-600 w-5">{index + 1}</span>
        <Icon size={14} aria-hidden="true" className="text-gray-600" />
        <span className="text-xs font-semibold text-gray-700">{label}</span>
        {sector.international && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-sky-100 text-sky-700">
            International · GST-free
          </span>
        )}
        {cost && <span className="ml-auto text-xs text-gray-700">{cost}</span>}
        {/* A ticketed flight can be cancelled with the airline. STX only, like booking: it moves
            money and cannot be undone. */}
        {onCancelFlight && sector.bookedFlight?.bookingId && !sector.flightCancelledAt && (
          <button
            onClick={() => onCancelFlight(index)}
            className="flex items-center gap-1 px-2.5 py-1 bg-red-700 text-white text-xs font-medium rounded-md ml-2 shrink-0 hover:bg-red-800"
            title="Cancel this flight with the airline"
          >
            Cancel flight
          </button>
        )}
        {sector.flightCancelledAt && (
          <span className="ml-2 shrink-0 inline-flex items-center rounded bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-700">
            Flight cancelled
          </span>
        )}
        {onSearchFlights && (
          <button
            onClick={flightBookingLocked ? undefined : onSearchFlights}
            disabled={flightBookingLocked}
            className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md ml-2 shrink-0 ${
              flightBookingLocked ? 'bg-gray-200 text-gray-600 cursor-not-allowed'
              : flightMode === 'quote' ? 'bg-amber-700 hover:bg-amber-800 text-white'
              : 'bg-sky-600 hover:bg-sky-700 text-white'
            }`}
            title={flightBookingLocked
              ? (flightLockReason || 'Flight booking is handled by STX — ask your STX contact to book this sector')
              : flightMode === 'quote'
                ? 'Search flights and save your chosen fare and price to this sector for the approver to review — this does not ticket anything'
                : 'Search and book a flight via the portal — fares expire in minutes, so this re-prices live before ticketing'}
            aria-label={flightBookingLocked
              ? (flightLockReason || 'Flight booking is handled by STX')
              : flightMode === 'quote'
                ? 'Search flights and save an option for approval'
                : 'Search flights for this sector'}
          >
            <Search size={11} aria-hidden="true" />
            {flightMode === 'quote'
              ? (hasPendingFlightOption(sector) ? 'Change option' : 'Save flight option')
              : (hasPendingFlightOption(sector) ? 'Refresh & book' : 'Search Flights')}
          </button>
        )}
        {onSearchHotels && (
          <button
            onClick={onSearchHotels}
            className={`flex items-center gap-1 px-2.5 py-1 text-white text-xs font-medium rounded-md ml-2 shrink-0 ${
              hotelMode === 'quote' ? 'bg-amber-700 hover:bg-amber-800' : 'bg-indigo-600 hover:bg-indigo-700'
            }`}
            title={hotelMode === 'quote'
              ? 'Search hotels and save your chosen room and price to this sector for the approver to review — this does not book anything'
              : 'Search and book a hotel via the portal — this will update this accommodation sector with your confirmed booking details'}
            aria-label={hotelMode === 'quote'
              ? 'Search hotels and save an option for approval'
              : 'Search hotels for this accommodation sector'}
          >
            <Search size={11} aria-hidden="true" />
            {hotelMode === 'quote'
              ? (hasPendingOption(sector) ? 'Change option' : 'Save hotel option')
              : (hasPendingOption(sector) ? 'Refresh & book' : 'Search Hotels')}
          </button>
        )}
      </div>
      <div className="px-4 py-3 space-y-1.5">
        {/* A saved-but-unbooked hotel option — shown to everyone, so an approver reviewing the
            trip sees exactly what is being proposed and that it is not yet booked. The action sits
            inside the card, directly under the line telling the reader to re-check. */}
        {hasPendingOption(sector) && (
          <div className="pb-2">
            <ProposedHotelCard
              option={sector.proposedHotel}
              approved={hotelMode === 'book'}
              actions={onSearchHotels && (
                <button
                  onClick={onSearchHotels}
                  className="flex items-center gap-1.5 px-3.5 py-2 bg-teal-700 text-white text-sm font-semibold rounded-lg hover:bg-teal-800"
                >
                  <RefreshCw size={13} aria-hidden="true" />
                  {hotelMode === 'book' ? 'Re-check availability & book' : 'Change hotel option'}
                </button>
              )}
            />
          </div>
        )}
        {/* Assistance the airline has to be told about separately. It stays on the sector until
            someone records the airline's confirmation, because the booking never carried it and
            nothing else would reveal that it had been forgotten. */}
        {(sector.specialAssistance || []).length > 0 && (
          <div className={`pb-2`}>
            <div className={`rounded-lg border p-3 ${
              hasUnarrangedSsr(sector) ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}`}>
              <div className="flex items-start gap-2">
                <AlertTriangle size={15} aria-hidden="true"
                  className={`shrink-0 mt-0.5 ${hasUnarrangedSsr(sector) ? 'text-red-700' : 'text-green-700'}`} />
                <div className="min-w-0 flex-1">
                  <p className={`text-xs font-bold uppercase tracking-wide ${
                    hasUnarrangedSsr(sector) ? 'text-red-800' : 'text-green-800'}`}>
                    {hasUnarrangedSsr(sector)
                      ? 'Special assistance — NOT yet arranged with the airline'
                      : 'Special assistance — arranged with the airline'}
                  </p>
                  <ul className="mt-1.5 space-y-0.5">
                    {ssrRequirements(sector.specialAssistance, sector.specialAssistanceOther).map(r => (
                      <li key={r.code} className="text-xs text-gray-800">
                        <span className="font-mono bg-white/70 px-1 py-0.5 rounded">{r.code}</span> {r.label}
                      </li>
                    ))}
                  </ul>

                  {hasUnarrangedSsr(sector) ? (
                    <>
                      <p className="mt-2 text-xs leading-relaxed text-red-900">
                        The flight booking cannot request assistance — the airline's API has no field
                        for it. Call the airline with the booking reference and record their
                        confirmation here.
                        {needsAdvanceNotice(sector.specialAssistance) && (
                          <strong> One of these needs advance notice or carriage can be refused.</strong>
                        )}
                      </p>
                      {canRecordSsr && (
                        <button
                          onClick={() => onRecordSsr(index)}
                          className="mt-2 px-3 py-1.5 bg-red-700 text-white text-xs font-semibold rounded-lg hover:bg-red-800"
                        >
                          Record airline confirmation
                        </button>
                      )}
                    </>
                  ) : (
                    <p className="mt-2 text-xs text-green-900">
                      Confirmed with the airline · reference <strong>{sector.ssrArrangedRef}</strong>
                      {sector.ssrArrangedByName ? ` · logged by ${sector.ssrArrangedByName}` : ''}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* A saved-but-unticketed flight — visible to the approver, who is being asked to
            approve this fare. The action sits in the card, under the line about re-pricing. */}
        {hasPendingFlightOption(sector) && (
          <div className="pb-2">
            <ProposedFlightCard
              option={sector.proposedFlight}
              approved={flightMode === 'book'}
              actions={onSearchFlights && !flightBookingLocked && (
                <button
                  onClick={onSearchFlights}
                  className="flex items-center gap-1.5 px-3.5 py-2 bg-sky-700 text-white text-sm font-semibold rounded-lg hover:bg-sky-800"
                >
                  <RefreshCw size={13} aria-hidden="true" />
                  {flightMode === 'book' ? 'Re-price & book' : 'Change flight option'}
                </button>
              )}
            />
          </div>
        )}
        {rows.filter(Boolean)}
        {sector.notes && (
          <div className="flex gap-2 pt-1">
            <span className="text-xs text-gray-600 w-32 shrink-0">Notes</span>
            <span className="text-xs text-gray-600 italic">{sector.notes}</span>
          </div>
        )}
      </div>
    </div>
  );
}


/**
 * Attach a booking's return leg to the sector at `sectorIndex`, replacing any return leg already
 * attached to it.
 *
 * Keyed to a stable `flightGroupId` on the outbound sector, NOT to the offer id: offer ids are
 * regenerated by every search, so re-pricing a saved option used to leave the old return sector
 * behind and add a second one. Also clears a stale return leg when a return booking is replaced
 * by a one-way.
 */
function applyReturnSector(sectors, sectorIndex, returnSector) {
  const out = [...sectors];
  const groupId = out[sectorIndex].flightGroupId || `fg_${Math.random().toString(36).slice(2, 10)}`;
  out[sectorIndex] = { ...out[sectorIndex], flightGroupId: groupId };

  // Drop whatever return leg this outbound previously owned. Legacy sectors written before
  // flightGroupId existed are matched on the route and date they duplicate, and only ever when
  // they are already flagged as a return leg — never a sector someone entered by hand.
  const stale = (x, i) => i !== sectorIndex && x.returnLegOf && (
    x.returnLegOf === groupId ||
    (returnSector && x.from === returnSector.from && x.to === returnSector.to && x.date === returnSector.date)
  );
  const cleaned = out.filter((x, i) => !stale(x, i));
  const idx = cleaned.findIndex(x => x.flightGroupId === groupId);

  if (!returnSector) return cleaned;
  cleaned.splice(idx + 1, 0, { ...returnSector, returnLegOf: groupId });
  return cleaned;
}

const feeLabelOf = (fee) =>
  fee.label
  || (fee.type === 'management' ? 'Management Fee' : fee.type === 'amendment' ? 'Amendment Fee' : 'Fee');

// Costed sectors and unwaived fees on a cancelled trip, as {index} lists.
// Both are billable independently: a cancellation or amendment fee often stands
// even when every sector was refunded.
const billableCandidates = (trip) => ({
  sectors: (trip?.sectors || []).map((s, i) => ({ s, i })).filter(({ s }) => parseFloat(s.cost) > 0),
  fees:    (trip?.fees    || []).map((f, i) => ({ f, i })).filter(({ f }) => !f.waived && parseFloat(f.amount) > 0),
});

function BillableItemsChecklist({ trip, sectorSel, feeSel, onToggleSector, onToggleFee }) {
  const { sectors, fees } = billableCandidates(trip);
  return (
    <div className="space-y-3">
      {sectors.length > 0 && (
        <div className="space-y-2">
          {sectors.map(({ s, i }) => {
            const cost = parseFloat(s.cost);
            const label = SECTOR_LABELS[s.type] || s.type;
            const summary = s.type === 'flight'
              ? [s.from, s.to].filter(Boolean).join(' → ')
              : s.type === 'accommodation'
              ? s.propertyName || ''
              : s.type === 'car-hire'
              ? [s.pickupLocation, s.dropOffLocation].filter(Boolean).join(' → ')
              : '';
            return (
              <label key={`s${i}`} className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sectorSel.has(i)}
                  onChange={() => onToggleSector(i)}
                  className="mt-0.5 accent-red-600"
                />
                <span className="text-sm text-gray-700">
                  <span className="font-medium">{label}</span>
                  {summary && <span className="text-gray-700"> — {summary}</span>}
                  <span className="text-gray-700 ml-1">A${cost.toFixed(2)}</span>
                </span>
              </label>
            );
          })}
        </div>
      )}

      {fees.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-700">Fees</p>
          {fees.map(({ f, i }) => {
            const amt     = parseFloat(f.amount) || 0;
            const incGST  = parseFloat((amt * (1 + (f.gstRate ?? 0.1))).toFixed(2));
            return (
              <label key={`f${i}`} className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={feeSel.has(i)}
                  onChange={() => onToggleFee(i)}
                  className="mt-0.5 accent-red-600"
                />
                <span className="text-sm text-gray-700">
                  <span className="font-medium">{feeLabelOf(f)}</span>
                  <span className="text-gray-700 ml-1">A${incGST.toFixed(2)} incl. GST</span>
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function TripDetail({ trip, clientId, onBack, onEdit, onAmend, onStatusChange, onUpdate, onDuplicate }) {
  const { userProfile } = useAuth();
  const { isSTX, clientConfig } = useTenant();
  const [acting, setActing] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [showDeclineInput, setShowDeclineInput] = useState(false);
  const [showAmendPrompt, setShowAmendPrompt] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [showReinstateModal, setShowReinstateModal] = useState(false);
  const [reinstateReason, setReinstateReason] = useState('');
  const [overrideStatus, setOverrideStatus] = useState('');
  const [overrideNotify, setOverrideNotify] = useState(false);
  const [cancelBillableSectors, setCancelBillableSectors] = useState(new Set());
  const [cancelBillableFees, setCancelBillableFees]       = useState(new Set());
  // For STX users: load the trip's client config (fees + cost centres + workflow)
  const [tripClientConfig, setTripClientConfig]           = useState(null);
  const [tripClientFees, setTripClientFees]               = useState(null);
  const [tripClientCostCentres, setTripClientCostCentres] = useState([]);
  const [feeConfigLoading, setFeeConfigLoading]           = useState(isSTX && !!clientId);
  useEffect(() => {
    if (!isSTX || !clientId) return;
    setFeeConfigLoading(true);
    getDoc(doc(db, 'clients', clientId, 'config', 'settings'))
      .then(snap => {
        const data = snap.exists() ? snap.data() : null;
        setTripClientConfig(data);
        setTripClientFees(data?.fees ?? null);
        setTripClientCostCentres(data?.dropdowns?.costCentres || []);
      })
      .catch(() => {})
      .finally(() => setFeeConfigLoading(false));
  }, [isSTX, clientId]);

  // The config that governs THIS trip. TenantContext leaves `clientConfig` null for STX staff
  // (they have no tenant of their own), so anything reading client workflow/feature settings must
  // fall back to the trip's own client config fetched above.
  const effectiveConfig = isSTX ? tripClientConfig : clientConfig;

  const costCentres = isSTX ? tripClientCostCentres : (clientConfig?.dropdowns?.costCentres || []);

  // Inline cost centre edit state
  const [showCCEdit, setShowCCEdit] = useState(false);
  const [newCC, setNewCC]           = useState('');
  const [ccReason, setCCReason]     = useState('');
  const [ccSaving, setCCSaving]     = useState(false);

  const handleCostCentreChange = async () => {
    if (!ccReason.trim()) return;
    setCCSaving(true);
    try {
      await onUpdate({
        costCentre: newCC,
        amendments: arrayUnion({
          at:     new Date().toISOString(),
          by:     userProfile?.uid || '',
          byName: [userProfile?.firstName, userProfile?.lastName].filter(Boolean).join(' ') || userProfile?.email || '',
          type:   'edit',
          note:   `Cost centre change reason: ${ccReason.trim()}`,
          changes: [`Cost centre: "${trip.costCentre || '(none)'}" → "${newCC || '(none)'}"` ],
        }),
      });
      setShowCCEdit(false);
      setCCReason('');
    } finally {
      setCCSaving(false);
    }
  };

  // Inline edit of the non-refundable items flagged for invoicing on a cancelled trip.
  // The cancel modal is the only other place these are set, so without this a trip
  // cancelled without ticking anything could never be invoiced.
  // { sectors: Set<number>, fees: Set<number> } while editing, else null
  const [billableEdit, setBillableEdit]     = useState(null);
  const [billableSaving, setBillableSaving] = useState(false);

  const cancelCandidates = billableCandidates(trip);

  const billableSectorLabel = (i) => {
    const s = trip.sectors?.[i];
    if (!s) return `Sector ${i + 1}`;
    const cost = parseFloat(s.cost) || 0;
    return `${SECTOR_LABELS[s.type] || s.type}${cost ? ` (A$${cost.toFixed(2)})` : ''}`;
  };

  const billableFeeLabel = (i) => {
    const f = trip.fees?.[i];
    if (!f) return `Fee ${i + 1}`;
    const incGST = parseFloat(((parseFloat(f.amount) || 0) * (1 + (f.gstRate ?? 0.1))).toFixed(2));
    return `${feeLabelOf(f)} (A$${incGST.toFixed(2)})`;
  };

  const handleBillableSave = async () => {
    const nextS = [...billableEdit.sectors].sort((a, b) => a - b);
    const nextF = [...billableEdit.fees].sort((a, b) => a - b);
    const prevS = [...(trip.billableSectorIndices || [])].sort((a, b) => a - b);
    const prevF = [...(trip.billableFeeIndices    || [])].sort((a, b) => a - b);

    const sectorsChanged = nextS.join(',') !== prevS.join(',');
    const feesChanged    = nextF.join(',') !== prevF.join(',');
    if (!sectorsChanged && !feesChanged) { setBillableEdit(null); return; }

    const fmt = (arr, labelFn) => (arr.length ? arr.map(labelFn).join(', ') : '(none)');
    const changes = [];
    if (sectorsChanged) {
      changes.push(`Non-refundable items to invoice: ${fmt(prevS, billableSectorLabel)} → ${fmt(nextS, billableSectorLabel)}`);
    }
    if (feesChanged) {
      changes.push(`Fees to invoice on cancellation: ${fmt(prevF, billableFeeLabel)} → ${fmt(nextF, billableFeeLabel)}`);
    }

    setBillableSaving(true);
    try {
      await onUpdate({
        billableSectorIndices: nextS,
        billableFeeIndices:    nextF,
        amendments: arrayUnion({
          at:     new Date().toISOString(),
          by:     userProfile?.uid || '',
          byName: [userProfile?.firstName, userProfile?.lastName].filter(Boolean).join(' ') || userProfile?.email || '',
          type:   'edit',
          note:   'Non-refundable items flagged for invoicing updated',
          changes,
        }),
      });
      setBillableEdit(null);
    } finally {
      setBillableSaving(false);
    }
  };

  // Provider rating state
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [existingRating, setExistingRating]   = useState(null);
  const [ratingLoaded, setRatingLoaded]       = useState(false);
  // Derived, not fetched — see resolveTravellerProfile. Was its own userId-only query, which
  // found nothing whenever the profile had no portal login attached.
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [checklistSaving, setChecklistSaving] = useState(false);
  const [localChecklist, setLocalChecklist] = useState(null);
  const [actualsEditing, setActualsEditing] = useState(false);
  const [actualCosts, setActualCosts] = useState({});
  const [actualsSubmitting, setActualsSubmitting] = useState(false);

  useEffect(() => {
    if (trip?.title) document.title = `${trip.title} — STX Connect`;
    return () => { document.title = 'Travel Management — STX Connect'; };
  }, [trip?.title]);

  useEffect(() => {
    if (!userProfile?.uid || !trip?.id) return;
    const docId = `${trip.id}_${userProfile.uid}`;
    getDoc(doc(db, 'tripFeedback', docId))
      .then(snap => setExistingRating(snap.exists() ? snap.data() : null))
      .catch(() => {})
      .finally(() => setRatingLoaded(true));
  }, [trip?.id, userProfile?.uid]);

  // Every passenger profile for this client, loaded ahead of the click because opening the
  // booking popup after an await trips popup blockers.
  //
  // ⚠️ Loaded UNCONDITIONALLY. This used to be skipped unless the trip had additional passengers,
  // which meant that on the common single-traveller trip the list was empty — so the name-based
  // fallback below had nothing to search and the traveller's own profile could never be found.
  const [tripPassengers, setTripPassengers] = useState([]);
  useEffect(() => {
    if (!clientId) { setTripPassengers([]); return; }
    getDocs(collection(db, 'clients', clientId, 'passengers'))
      .then(snap => setTripPassengers(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => {});
  }, [clientId]);

  const travellerPassenger = useMemo(
    () => resolveTravellerProfile(trip, tripPassengers),
    [trip?.travellerId, trip?.travellerName, tripPassengers] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const role = userProfile?.role;
  const canEdit = ['stx_admin', 'stx_ops', 'client_ops', 'client_traveller'].includes(role);
  const canEditCostCentre = ['stx_admin', 'stx_ops', 'client_ops'].includes(role);

  // Determine if this user can approve THIS specific trip, respecting their approval scope.
  const approvalScope = useApprovalScope(userProfile, clientId);
  const isApprover = ['stx_admin', 'stx_ops'].includes(role) || matchesApprovalScope(approvalScope, trip, userProfile?.uid);
  // client_ops and client_approver can book self-managed trips on behalf of travellers
  const canBook = ['stx_admin', 'stx_ops', 'client_ops', 'client_approver', 'client_traveller'].includes(role);

  // ⚠️ For STX the trip's client config is fetched asynchronously and is null until it lands,
  // and tripRequiresApproval() treats a missing config as "approval required". Clicking before it
  // arrives therefore opened the booking flow in QUOTE mode on a client that needs no approval —
  // silently, and looking exactly like the setting had not been saved. Nothing that reads the
  // workflow may run until we actually know what it says.
  const configReady = !isSTX || !feeConfigLoading;

  // Hotel search via Nuitee. Approved trips book outright; trips still awaiting approval save an
  // option for the approver.
  const hotelBookingEnabledForClient =
    effectiveConfig?.features?.hotelBooking !== false &&
    effectiveConfig?.hotelBooking?.selfManagedHotelBooking !== false;
  const hotelMode = hotelFlowMode(trip.status, tripRequiresApproval(effectiveConfig, trip.tripType));
  const hotelStageAllowed =
    trip.status === 'approved' ||
    (hotelMode === 'quote' && ['draft', 'pending_approval'].includes(trip.status));
  // STX can search on any trip type, matching TripForm — otherwise an option saved from the edit
  // form on an STX-Managed trip could never be re-checked or booked from the trip page.
  // Client users stay limited to self-managed trips they have booking access to.
  const canSearchHotels =
    configReady && hotelStageAllowed &&
    (isSTX || (trip.tripType?.toLowerCase() === 'self-managed' && canBook && hotelBookingEnabledForClient));

  // Flights mirror the hotel gate on mode: an unapproved trip saves a fare for the approver, an
  // approved one re-prices live and tickets.
  const flightMode = flightFlowMode(trip.status, tripRequiresApproval(effectiveConfig, trip.tripType));
  // Who may ticket. Issuing a ticket is irreversible in a way a hotel booking is not — a fare
  // ticketed in the wrong name usually means buying another one — so client staff get it only
  // where STX has deliberately switched it on for that client. Strictly `=== true`: absent and
  // false both mean off, so no client gains access by having never been configured. STX are never
  // gated. Client staff who do NOT have it still see the button, disabled, so they know to ask.
  const flightBookingEnabledForClient = effectiveConfig?.features?.flightBooking === true;
  const canSearchFlights = ['draft', 'pending_approval', 'approved'].includes(trip.status);
  const flightBookingLocked =
    !configReady || (!isSTX && !(canBook && flightBookingEnabledForClient));
  const flightLockReason = !configReady
    ? 'Checking this client\'s booking settings…'
    : 'Flight booking is handled by STX — ask your STX contact to book this sector';

  const [cancelSector, setCancelSector] = useState(null);

  /**
   * Record a CONFIRMED cancellation on the sector. Only ever called once the modal has re-read the
   * booking and seen a cancelled status — an unconfirmed attempt deliberately writes nothing, so
   * the trip never claims a cancellation that did not happen.
   */
  const recordFlightCancelled = async (i, result) => {
    const sectors = [...(trip.sectors || [])];
    sectors[i] = {
      ...sectors[i],
      flightCancelledAt:     new Date().toISOString(),
      flightCancelledBy:     userProfile?.uid || '',
      flightCancelledByName: [userProfile?.firstName, userProfile?.lastName].filter(Boolean).join(' ')
        || userProfile?.email || '',
      flightCancelStatus:    result?.status || '',
      flightRefundQuoted:    result?.refund ? `${result.refund.currency} ${result.refund.amount}` : '',
    };
    try {
      await updateDoc(doc(db, 'clients', clientId, 'trips', trip.id), {
        sectors,
        amendments: arrayUnion({
          at:     new Date().toISOString(),
          by:     userProfile?.uid || '',
          byName: [userProfile?.firstName, userProfile?.lastName].filter(Boolean).join(' ') || userProfile?.email || '',
          type:   'edit',
          note:   `Flight cancelled with the airline — ${sectors[i].bookingRef || 'no reference'}`,
          // Strings only: the renderer has a legacy guard but new entries must be plain strings.
          changes: [`Flight ${sectors[i].flightNumber || ''} cancelled`
            + (result?.refund ? ` · refund quoted ${result.refund.currency} ${result.refund.amount}` : '')],
        }),
        updatedAt: new Date().toISOString(),
      });
    } catch { /* the airline cancellation stands regardless; this is the portal's record */ }
  };

  const [ssrSector, setSsrSector] = useState(null);
  const [ssrRef, setSsrRef]       = useState('');
  const [ssrSaving, setSsrSaving] = useState(false);

  /** Log the airline's confirmation against the sector, so it stops reading as outstanding. */
  const saveSsrConfirmation = async () => {
    if (!ssrRef.trim()) return;
    setSsrSaving(true);
    try {
      const sectors = [...(trip.sectors || [])];
      sectors[ssrSector] = {
        ...sectors[ssrSector],
        ssrArrangedRef:    ssrRef.trim(),
        ssrArrangedAt:     new Date().toISOString(),
        ssrArrangedBy:     userProfile?.uid || '',
        ssrArrangedByName: [userProfile?.firstName, userProfile?.lastName].filter(Boolean).join(' ')
          || userProfile?.email || '',
      };
      await updateDoc(doc(db, 'clients', clientId, 'trips', trip.id), {
        sectors, updatedAt: new Date().toISOString(),
      });
      setSsrSector(null);
    } catch { /* leave the dialog open so it can be retried */ }
    finally { setSsrSaving(false); }
  };

  /**
   * A paid Nuitee booking moves the trip on to `booked`.
   *
   * TripForm has always done this on save (it sets status from `flightBooked`/`nuiteeBooked`),
   * but bookings made from this page are written straight to Firestore and skipped it — so a
   * ticketed, paid-for flight left the trip sitting at "Approved" and the traveller never got a
   * booking confirmation, because notifyOnTripBooked fires on the transition that never happened.
   *
   * Only from `approved`. A trip still awaiting approval should not be restatused by a booking,
   * and one already `booked` or `cancelled` is not ours to move.
   */
  const bookedTransition = (isBooking, what) => {
    if (!isBooking || trip.status !== 'approved') return {};
    return {
      status: 'booked',
      amendments: arrayUnion({
        at:     new Date().toISOString(),
        by:     userProfile?.uid || '',
        byName: [userProfile?.firstName, userProfile?.lastName].filter(Boolean).join(' ') || userProfile?.email || '',
        type:   'status_change',
        from:   'approved',
        to:     'booked',
        note:   `${what} booked and paid via Nuitee`,
      }),
    };
  };

  const flightWindowRef = useRef(null);

  // The flight-result handler re-registers only when the trip or sectors change, so it would
  // otherwise read whatever these held at that moment — both arrive asynchronously and are
  // routinely still empty then. A ref refreshed each render keeps it on the current values.
  const passengerCtxRef = useRef({ profiles: [], travellerProfile: null });
  useEffect(() => {
    passengerCtxRef.current = { profiles: tripPassengers, travellerProfile: travellerPassenger };
  });

  useEffect(() => {
    if (!canSearchFlights) return;
    const handleMessage = async (e) => {
      if (e.origin !== window.location.origin) return;
      const isBooking = e.data?.type === 'FLIGHT_BOOKED';
      const isOption  = e.data?.type === 'FLIGHT_OPTION_SAVED';
      if (!isBooking && !isOption) return;
      const { sectorIndex } = e.data;
      const payload = isBooking ? e.data.bookingData : e.data.optionData;
      if (typeof sectorIndex !== 'number' || !payload) return;

      const sectors = [...(trip.sectors || [])];
      const sector = sectors[sectorIndex];
      if (!sector) return;
      sectors[sectorIndex] = isBooking
        ? sectorPatchFromFlightBooking(sector, payload)
        : sectorPatchFromFlightOption(sector, payload);

      // A return is one fare over two flights: the price stays whole on the outbound and the
      // return leg gets its own zero-cost sector so it still shows in the itinerary and reports.
      const withReturn = applyReturnSector(sectors, sectorIndex, payload.returnSector || null);

      // Anyone put on the booking who is not already on the trip is added to it, against this
      // sector. Otherwise the trip says one traveller while the fare covers two, and the support
      // worker report and cost allocation both read as though they never flew.
      // Matched on profile id and on every spelling of a name, never on the ticketed name alone:
      // the trip names people by their preferred name while the ticket carries the legal one.
      const { profiles, travellerProfile } = passengerCtxRef.current;
      const addedPassengers = newBookingPassengers(payload.passengers, {
        travellerName: trip.travellerName,
        travellerProfile,
        additionalPassengers: trip.additionalPassengers,
        profiles,
      })
        .map(p => ({
          name: p.name,
          passengerId: p.passengerId || '',
          costCentre: '',
          role: p.role || 'traveller',
          sectorIndices: [sectorIndex],
          allocatedCost: 0,
        }));

      try {
        await updateDoc(doc(db, 'clients', clientId, 'trips', trip.id), {
          sectors: withReturn,
          ...(addedPassengers.length > 0 ? {
            additionalPassengers: [...(trip.additionalPassengers || []), ...addedPassengers],
          } : {}),
          ...bookedTransition(isBooking, 'Flight'),
          updatedAt: new Date().toISOString(),
        });
      } catch { /* popup has no UI to surface this; the trip simply stays unchanged */ }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [canSearchFlights, trip.sectors, trip.status, trip.id, clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openFlightBookingFromDetail = (sectorIndex) => {
    const sector = (trip.sectors || [])[sectorIndex];
    const params = new URLSearchParams({
      tripId:      trip.id || '',
      sectorIndex: String(sectorIndex),
      clientId:    clientId || '',
      origin:      sector?.from || trip.originCity || '',
      destination: sector?.to   || trip.destinationCity || '',
      date:        sector?.date || trip.startDate || '',
      // The sector's Airline field is free text ('Qantas'), and the rates API has no carrier
      // filter — so it pre-selects the results filter rather than narrowing the request.
      airline:     sector?.airline || '',
      ssr:      JSON.stringify(sector?.specialAssistance || []),
      ssrOther: sector?.specialAssistanceOther || '',
      // Frequent flyer numbers from the profile. `program` is free text, so the flow
      // resolves it to an IATA code and lets STX correct it before anything is sent.
      paxLoyalty: JSON.stringify(travellerPassenger?.loyaltyPrograms || []),
      travellerName: trip.travellerName || '',
      // Sent so the popup can resolve the profile itself if this screen's own lookup missed — see
      // the profile-recovery effect in FlightBookingFlow.
      travellerId:   trip.travellerId || '',
      email:         travellerPassenger?.email || '',
      paxFirstName:  travellerPassenger?.firstName  || '',
      paxLastName:   travellerPassenger?.lastName   || '',
      paxMiddleName: travellerPassenger?.middleName || '',
      paxDob:        travellerPassenger?.dateOfBirth || '',
      paxGender:     travellerPassenger?.gender || '',
      paxPhone:      travellerPassenger?.phone || '',
      // Identity documents from the profile, so the traveller's passport or licence does not
      // have to be retyped for every booking. Trimmed to what the airline needs.
      paxDocs: JSON.stringify((travellerPassenger?.identityDocuments || [])
        .filter(d => d && (d.number || '').trim())
        .map(d => ({ type: d.type, number: d.number, expiry: d.expiry, notes: d.notes }))),
      // Everyone on this sector — a support worker or carer flies on the same booking, not a
      // separate one, or they can end up on a different fare and not seated together.
      pax: JSON.stringify(
        flightPassengersFor(trip, sectorIndex, tripPassengers, travellerPassenger)
          .map(flightPassengerParam)),
      mode:          flightMode,
      // Written straight to Firestore from this screen — no Save step to prompt for.
      openedFrom:    'detail',
    });
    if (flightMode === 'book' && hasPendingFlightOption(sector)) {
      params.set('savedOption', encodeSavedFlight(sector.proposedFlight));
    }
    // Bulky context — passengers, documents, loyalty, a saved option — is stashed under a short
    // handle rather than travelling on the query string. See utils/bookingContext for why.
    const url = buildBookingUrl('/flight-booking', params, BOOKING_URL_KEYS);
    if (flightWindowRef.current && !flightWindowRef.current.closed) {
      flightWindowRef.current.focus();
    } else {
      flightWindowRef.current = window.open(url, '_blank', 'width=1200,height=800');
    }
  };

  const hotelWindowRef = useRef(null);
  const [nuiteeBookedSector, setNuiteeBookedSector] = useState(null); // { index, propertyName, isOption }

  useEffect(() => {
    if (!canSearchHotels) return;
    const handleMessage = async (e) => {
      if (e.origin !== window.location.origin) return;
      const isBooking = e.data?.type === 'HOTEL_BOOKED';
      const isOption  = e.data?.type === 'HOTEL_OPTION_SAVED';
      if (!isBooking && !isOption) return;
      const { sectorIndex } = e.data;
      const payload = isBooking ? e.data.bookingData : e.data.optionData;
      if (typeof sectorIndex !== 'number' || !payload) return;
      const sectors = [...(trip.sectors || [])];
      const sector = sectors[sectorIndex];
      if (!sector) return;
      sectors[sectorIndex] = isBooking
        ? sectorPatchFromBooking(sector, payload)
        : sectorPatchFromOption(sector, payload);
      try {
        await updateDoc(doc(db, 'clients', clientId, 'trips', trip.id), {
          sectors,
          ...bookedTransition(isBooking, 'Hotel'),
          updatedAt: new Date().toISOString(),
        });
        setNuiteeBookedSector({ index: sectorIndex, propertyName: payload.propertyName || '', isOption });
      } catch { /* silently ignore — the window is a popup, no UI to show error */ }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [canSearchHotels, trip.sectors, trip.status, trip.id, clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openHotelBookingFromDetail = (sectorIndex) => {
    const sector = (trip.sectors || [])[sectorIndex];
    const params = new URLSearchParams({
      tripId:          trip.id || '',
      sectorIndex:     String(sectorIndex),
      tripType:        trip.tripType || '',
      clientId:        clientId || '',
      checkin:         sector?.checkIn  || trip.startDate || '',
      checkout:        sector?.checkOut || trip.endDate   || '',
      travellerName:   trip.travellerName || '',
      destinationCity: trip.destinationCity || '',
      email:           travellerPassenger?.email || '',
      mode:            hotelMode,
      // The traveller's recorded access needs ride across so the search can pre-select the
      // filters it can actually check, and show the rest as things to confirm with the property.
      accessNeeds:     JSON.stringify(sector?.accessibilityRequirements || []),
      accessNotes:     sector?.accessibilityNotes || '',
    });
    // Booking an already-quoted room: hand the saved option across so the flow reopens that hotel
    // and shows how the live price compares with what was approved.
    if (hotelMode === 'book' && hasPendingOption(sector)) {
      params.set('savedOption', encodeSavedOption(sector.proposedHotel));
    }
    const url = buildBookingUrl('/hotel-booking', params, BOOKING_URL_KEYS);
    if (hotelWindowRef.current && !hotelWindowRef.current.closed) {
      hotelWindowRef.current.focus();
    } else {
      hotelWindowRef.current = window.open(url, '_blank', 'width=1200,height=800');
    }
  };

  // Amendment fee: STX users use the trip's client config, client users use their own
  const isDraftOrDeclined = ['draft', 'declined'].includes(trip.status);
  const feeConfig = isSTX ? tripClientFees : clientConfig?.fees;
  const amendFeeAmount  = feeConfig?.amendmentFeeAmount || 0;
  const amendFeeGST     = parseFloat((amendFeeAmount * (1 + (feeConfig?.gstRate ?? 0.1))).toFixed(2));
  const amendFeeAppliesTo = feeConfig?.amendmentFeeAppliesTo || [];
  const amendmentFeeApplies = !isDraftOrDeclined
    && feeConfig?.amendmentFeeEnabled
    && amendFeeAmount > 0
    && (amendFeeAppliesTo.length === 0 || amendFeeAppliesTo.includes(trip.tripType));

  const act = async (newStatus, extra = {}) => {
    setActing(true);
    try {
      await onStatusChange(trip, newStatus, extra);

      const now       = new Date().toISOString();
      const tripId    = trip.id;
      const tripTitle = trip.title || '';
      const cid       = clientId || '';

      const queue = (doc) => addDoc(collection(db, 'emailQueue'), {
        status: 'pending', createdAt: now, clientId: cid, tripId, tripTitle, ...doc,
      });

      // trip_submitted is NOT queued here — the notifyApproversOnTripSubmitted trigger fires on the
      // status transition instead, so submitting from the trip form notifies approvers too. Queueing
      // it here as well would send every approver a duplicate.

      // trip_approved is NOT queued here either — notifyOnTripApproved fires on the status
      // transition and queues both the traveller's email and the STX one, so trips that approve
      // without passing through this screen still notify everyone.

      if (newStatus === 'declined') {
        // Notify creator (if not STX) + traveller, with decline reason
        await queue({
          type:          'trip_declined',
          recipientId:   trip.createdBy   || '',
          travellerId:   trip.travellerId || '',
          declineReason: extra.declineReason || '',
          scheduledFor:  now,
        });
      }

      if (newStatus === 'cancelled' && !isSTX) {
        await queue({
          type:             'trip_cancelled_by_client',
          cancellationReason: extra.cancellationReason || '',
          cancelledByName:  [userProfile?.firstName, userProfile?.lastName].filter(Boolean).join(' ') || userProfile?.email || '',
          scheduledFor:     now,
        });
      }

      // trip_booked and its two scheduled follow-ups are queued by notifyOnTripBooked on the status
      // transition, so booking through the trip form sends them too.
    } finally {
      setActing(false);
      setShowDeclineInput(false);
      setDeclineReason('');
    }
  };

  const sectorCostInclGST = (trip.sectors || []).reduce((sum, s) => sum + (parseFloat(s.cost) || 0), 0);
  const feesInclGST = (trip.fees || []).reduce((sum, f) => sum + (parseFloat(f.amount) || 0) * (1 + (f.gstRate ?? 0.1)), 0);
  const totalCost = sectorCostInclGST + feesInclGST;

  const sectorCostExGST = (trip.sectors || []).reduce((sum, s) => {
    const c = parseFloat(s.cost) || 0;
    return sum + (s.international ? c : c / 1.1);
  }, 0);
  const feesExGST = (trip.fees || []).reduce((sum, f) => sum + (parseFloat(f.amount) || 0), 0);
  const totalExGST = sectorCostExGST + feesExGST;

  // Travel credit already paid on an earlier trip and applied here. It doesn't change what the
  // services cost — the sectors above stay at full price — it changes what the client is billed.
  const creditInclGST = tripCreditInclGST(trip);
  const creditExGST   = tripCreditExGST(trip);
  const netTotalInclGST = Math.max(0, totalCost   - creditInclGST);
  const netTotalExGST   = Math.max(0, totalExGST  - creditExGST);

  const wasCancelled = trip.status === 'cancelled';
  // Who can set a status by hand. Cancelled trips need a way back, and client_ops get it because
  // they can cancel in the first place. On a live trip this bypasses the workflow entirely — it
  // could mark a trip approved without an approver — so that stays with STX.
  const canOverrideStatus = wasCancelled ? (isSTX || role === 'client_ops') : isSTX;

  // What the trip was before it was cancelled — the default, not the only choice.
  // handleStatusChange records `from` on every status_change amendment, so this is recovered rather
  // than guessed. Falls back to approved for trips cancelled before that history existed.
  const preCancelStatus = (() => {
    const cancels = (trip.amendments || []).filter(a => a.type === 'status_change' && a.to === 'cancelled');
    const from = cancels[cancels.length - 1]?.from;
    return ['draft', 'pending_approval', 'approved', 'booked'].includes(from) ? from : 'approved';
  })();

  // travelling and completed are derived from booked + dates, so they are never set directly.
  const OVERRIDE_STATUSES = ['draft', 'pending_approval', 'approved', 'declined', 'booked', 'cancelled'];

  const handleDeleteFee = async (fee) => {
    const feeLabel = fee.label || (fee.type === 'amendment' ? 'Amendment fee' : 'Management fee');
    await onUpdate({
      fees: arrayRemove(fee),
      amendments: arrayUnion({
        at: new Date().toISOString(),
        by: userProfile?.uid || '',
        byName: [userProfile?.firstName, userProfile?.lastName].filter(Boolean).join(' ') || userProfile?.email || '',
        type: 'fee_removed',
        note: 'Fee removed',
        changes: [`${feeLabel} removed (A$${(fee.amount || 0).toFixed(2)} ex-GST)`],
      }),
    });
  };

  const handleSaveActuals = async () => {
    setActualsSubmitting(true);
    try {
      const updatedSectors = (trip.sectors || []).map((s, i) => ({
        ...s,
        actualCost: actualCosts[i] !== '' && actualCosts[i] != null ? parseFloat(actualCosts[i]) : s.actualCost,
      }));
      const changes = (trip.sectors || []).map((s, i) => {
        const est = parseFloat(s.cost) || 0;
        const actual = parseFloat(actualCosts[i]);
        if (isNaN(actual) || Math.abs(est - actual) < 0.01) return null;
        return `${SECTOR_LABELS[s.type] || s.type} actual: A$${est.toFixed(2)} (est.) → A$${actual.toFixed(2)}`;
      }).filter(Boolean);
      await onUpdate({
        sectors: updatedSectors,
        actualsRecorded: true,
        amendments: arrayUnion({
          at: new Date().toISOString(),
          by: userProfile?.uid || '',
          byName: [userProfile?.firstName, userProfile?.lastName].filter(Boolean).join(' ') || userProfile?.email || '',
          type: 'edit',
          note: 'Actual costs recorded',
          changes,
        }),
      });
      setActualsEditing(false);
    } finally {
      setActualsSubmitting(false);
    }
  };

  return (
    <div>
      {/* Back + actions header */}
      <div className="flex items-center gap-3 mb-5">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-gray-700 hover:text-gray-900"
        >
          <ArrowLeft size={15} aria-hidden="true" /> Back to trips
        </button>
        <div className="ml-auto flex items-center gap-2">
          {/* Rate providers — booked trips, any traveller */}
          {trip.status === 'booked' && ratingLoaded && (
            <button
              onClick={() => setShowRatingModal(true)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                existingRating
                  ? 'border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100'
                  : 'border-teal-300 text-teal-700 bg-teal-50 hover:bg-teal-100'
              }`}
            >
              <Star size={13} fill={existingRating ? 'currentColor' : 'none'} />
              {existingRating ? 'Update rating' : 'Rate providers'}
            </button>
          )}

          {/* Duplicate trip — users who can create trips */}
          {canEdit && onDuplicate && (
            <button
              onClick={() => onDuplicate(trip)}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-sm rounded-lg text-gray-700 hover:bg-gray-50"
            >
              <Copy size={13} aria-hidden="true" /> Duplicate
            </button>
          )}

          {/* Add to calendar — approved / booked / travelling trips */}
          {['approved', 'booked', 'travelling'].includes(getDisplayStatus(trip)) && (
            <button
              onClick={() => {
                const ics = generateICS(trip);
                const blob = new Blob([ics], { type: 'text/calendar' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${(trip.title || 'trip').replace(/[^a-z0-9]/gi, '-')}.ics`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-sm rounded-lg text-gray-700 hover:bg-gray-50"
            >
              <Calendar size={13} aria-hidden="true" /> Add to calendar
            </button>
          )}

          {/* Draft/Declined → plain Edit; submitted/approved/booked → Amend (with fee prompt) */}
          {canEdit && trip.status !== 'cancelled' && isDraftOrDeclined && (
            <button
              onClick={() => onEdit(trip)}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-sm rounded-lg hover:bg-gray-50"
            >
              <Edit2 size={13} /> Edit
            </button>
          )}
          {canEdit && trip.status !== 'cancelled' && !isDraftOrDeclined && (
            <button
              onClick={() => setShowAmendPrompt(true)}
              disabled={feeConfigLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <Edit2 size={13} /> Amend
            </button>
          )}

          {/* Submit — draft only, by creator/ops */}
          {canEdit && trip.status === 'draft' && (
            <button
              onClick={() => act(tripRequiresApproval(effectiveConfig, trip.tripType) ? 'pending_approval' : 'approved')}
              disabled={acting}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Send size={13} /> Submit for approval
            </button>
          )}

          {/* Approve + Decline — pending_approval */}
          {isApprover && trip.status === 'pending_approval' && !showDeclineInput && (
            <>
              <button
                onClick={() => act('approved')}
                disabled={acting}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                <CheckCircle size={13} /> Approve
              </button>
              <button
                onClick={() => setShowDeclineInput(true)}
                disabled={acting}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                <XCircle size={13} /> Decline
              </button>
            </>
          )}

          {/* Mark as Booked — approved trips only.
              STX can always book any trip type.
              client_ops, client_approver, client_traveller can book self-managed trips only. */}
          {trip.status === 'approved' && (isSTX || (canBook && trip.tripType?.toLowerCase() === 'self-managed')) && (
            <button
              onClick={() => {
                const checklist = localChecklist || trip.bookingChecklist;
                if (isSTX && checklist && checklist.some(item => !item.checked && !item.waived)) {
                  if (!window.confirm('Some pre-booking checklist items are incomplete. Proceed with booking anyway?')) return;
                }
                act('booked');
              }}
              disabled={acting}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              <CheckCircle size={13} /> Mark as booked
            </button>
          )}

          {/* Cancel — STX always, client_ops for their client's trips */}
          {['approved', 'booked'].includes(trip.status) && (isSTX || role === 'client_ops') && !showCancelModal && (
            <button
              onClick={() => { setCancelReason(''); setCancelBillableSectors(new Set()); setShowCancelModal(true); }}
              disabled={acting}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-sm rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            >
              <Ban size={13} /> Cancel trip
            </button>
          )}

          {/* Manual status override. On cancelled trips this is the way back, and client_ops get it
              too since they can cancel. On live trips it's a correction tool, so STX only. */}
          {canOverrideStatus && !showReinstateModal && (
            <button
              onClick={() => {
                setReinstateReason('');
                // Cancelled trips default to where they came from; otherwise just the first option
                // that isn't the status the trip already has.
                setOverrideStatus(wasCancelled ? preCancelStatus : OVERRIDE_STATUSES.find(s => s !== trip.status));
                setOverrideNotify(false);
                setShowReinstateModal(true);
              }}
              disabled={acting}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg disabled:opacity-50 ${
                trip.status === 'cancelled'
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <RotateCcw size={13} aria-hidden="true" />
              {trip.status === 'cancelled' ? 'Reinstate trip' : 'Change status'}
            </button>
          )}
        </div>
      </div>

      {/* Status override / reinstate */}
      {showReinstateModal && (
        <div className="mb-4 rounded-xl border border-green-300 bg-green-50 p-4">
          <p className="text-sm font-semibold text-green-900 mb-1">
            {wasCancelled ? 'Reinstate this trip' : 'Change this trip’s status'}
          </p>
          <p className="text-xs text-gray-700 mb-3">
            {wasCancelled
              ? `Choose the status it should return to. It defaults to ${STATUS_CONFIG[preCancelStatus]?.label || preCancelStatus}, the status it held before it was cancelled, but pick whichever is actually correct.`
              : 'Set the status directly. Use this to correct a status set in error — it skips the normal workflow, so the change is recorded in the amendment history.'}
          </p>

          <label htmlFor="override-status" className="block text-xs font-medium text-gray-700 mb-1">
            New status *
          </label>
          <select
            id="override-status"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 mb-3"
            value={overrideStatus}
            onChange={e => setOverrideStatus(e.target.value)}
          >
            {OVERRIDE_STATUSES.filter(s => s !== trip.status).map(s => (
              <option key={s} value={s}>
                {STATUS_CONFIG[s]?.label || s}{s === preCancelStatus && wasCancelled ? ' — status before cancellation' : ''}
              </option>
            ))}
          </select>

          <label htmlFor="reinstate-reason" className="block text-xs font-medium text-gray-700 mb-1">
            Reason *
          </label>
          <input
            id="reinstate-reason"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 placeholder:text-gray-500 mb-3"
            value={reinstateReason}
            onChange={e => setReinstateReason(e.target.value)}
            placeholder={wasCancelled ? 'e.g. cancelled in error — still needs approval' : 'e.g. marked booked by mistake'}
          />

          <label className="flex items-start gap-2 mb-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={overrideNotify}
              onChange={e => setOverrideNotify(e.target.checked)}
              className="mt-0.5 rounded border-gray-300 text-green-600 focus:ring-green-500 shrink-0"
            />
            <span className="text-xs text-gray-700 leading-relaxed">
              Send the usual notifications for the new status.
              {' '}Off by default — corrections normally shouldn’t re-notify people who were already told.
              {overrideNotify && overrideStatus === 'booked' && (
                <span className="block mt-1 text-amber-800 font-medium">
                  Booked also re-queues the pre-departure reminder and the post-trip rating request.
                  If this trip was booked before, those are already queued and travellers will get them twice.
                </span>
              )}
            </span>
          </label>

          <div className="flex gap-2">
            <button
              onClick={async () => {
                await act(overrideStatus, {
                  reinstateReason: reinstateReason.trim(),
                  // Stamped on the write itself so the notification triggers can tell this exact
                  // change was a silent correction.
                  ...(overrideNotify ? {} : { silentStatusChangeAt: new Date().toISOString() }),
                  // Clear cancellation state whenever we move off cancelled
                  ...(wasCancelled ? { cancellationReason: '', cancelledAt: null, billableSectorIndices: [], billableFeeIndices: [] } : {}),
                });
                setShowReinstateModal(false);
              }}
              disabled={!reinstateReason.trim() || !overrideStatus || acting}
              className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {acting ? 'Saving…' : `Set to ${STATUS_CONFIG[overrideStatus]?.label || overrideStatus}`}
            </button>
            <button
              onClick={() => setShowReinstateModal(false)}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Policy variance breach notice */}
      {trip.policyVarianceBreached && (trip.varianceBreaches || []).length > 0 && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={15} className="text-red-600 shrink-0" />
            <p className="text-sm font-semibold text-red-800">Policy variance exceeded</p>
          </div>
          <div className="space-y-1 mb-2">
            {(trip.varianceBreaches || []).map((b, i) => (
              <p key={i} className="text-xs text-gray-700 pl-5">
                <span className="font-medium">{b.label}</span>
                {b.city && ` (${b.city})`}:
                {' '}A${(b.cost || 0).toFixed(2)} {b.unit} vs policy A${(b.policyRate || 0).toFixed(2)}
                {' '}— <span className="font-medium">{(b.excessPct || 0) > 0 ? '+' : ''}{(b.excessPct || 0).toFixed(1)}% over policy</span>
                {' '}(threshold A${(b.threshold || 0).toFixed(2)})
              </p>
            ))}
          </div>
          {trip.status === 'pending_approval' && (
            <p className="text-xs text-red-700 pl-5 font-medium">
              This trip requires explicit approval to proceed due to the policy variance above.
            </p>
          )}
        </div>
      )}

      {/* Amend prompt — fee decision before opening form */}
      {showAmendPrompt && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm font-medium text-amber-800 mb-2">Amend trip</p>
          {amendmentFeeApplies ? (
            <p className="text-sm text-amber-700 mb-3">
              An amendment fee of <strong>A${amendFeeAmount.toFixed(2)} ex-GST</strong> (A${amendFeeGST.toFixed(2)} inc. GST)
              applies to <strong>{trip.tripType}</strong> trips. Would you like to include it?
            </p>
          ) : (
            <p className="text-sm text-amber-700 mb-3">
              This will open the trip for editing. The change will be recorded in the trip history.
            </p>
          )}
          <div className="flex gap-2 flex-wrap">
            {amendmentFeeApplies ? (
              <>
                <button
                  onClick={() => { setShowAmendPrompt(false); onAmend(trip, { apply: true, amount: amendFeeAmount, gstRate: feeConfig?.gstRate ?? 0.1 }); }}
                  className="px-3 py-1.5 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700"
                >
                  Include fee (A${amendFeeAmount.toFixed(2)})
                </button>
                <button
                  onClick={() => { setShowAmendPrompt(false); onAmend(trip, { apply: false, amount: amendFeeAmount, gstRate: feeConfig?.gstRate ?? 0.1 }); }}
                  className="px-3 py-1.5 border border-amber-400 text-amber-800 text-sm rounded-lg hover:bg-amber-100"
                >
                  Waive fee
                </button>
              </>
            ) : (
              <button
                onClick={() => { setShowAmendPrompt(false); onAmend(trip, null); }}
                className="px-3 py-1.5 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700"
              >
                Continue
              </button>
            )}
            <button
              onClick={() => setShowAmendPrompt(false)}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Decline reason input */}
      {showDeclineInput && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm font-medium text-red-700 mb-2">Decline reason</p>
          <textarea
            className="w-full border border-red-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
            rows={2}
            value={declineReason}
            onChange={e => setDeclineReason(e.target.value)}
            placeholder="Reason for declining (optional but recommended)"
          />
          <div className="flex gap-2 mt-2">
            <button
              onClick={() => act('declined', { declineReason })}
              disabled={acting}
              className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              Confirm decline
            </button>
            <button
              onClick={() => { setShowDeclineInput(false); setDeclineReason(''); }}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Cancel modal */}
      {showCancelModal && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm font-medium text-red-800 mb-1">Cancel trip</p>
          <p className="text-sm text-red-700 mb-3">
            {isSTX
              ? 'Provide a reason and select any non-refundable items that still need to be invoiced.'
              : 'Provide a reason for cancellation. STX will be notified to review for any invoicing.'}
          </p>
          <textarea
            className="w-full border border-red-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 mb-3"
            rows={2}
            value={cancelReason}
            onChange={e => setCancelReason(e.target.value)}
            placeholder="Reason for cancellation (required)"
          />

          {/* STX only: billability checklist — sectors and fees */}
          {isSTX && (cancelCandidates.sectors.length > 0 || cancelCandidates.fees.length > 0) && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-red-700 mb-2 uppercase tracking-wide">
                Non-refundable items to invoice:
              </p>
              <BillableItemsChecklist
                trip={trip}
                sectorSel={cancelBillableSectors}
                feeSel={cancelBillableFees}
                onToggleSector={i => setCancelBillableSectors(prev => {
                  const next = new Set(prev);
                  if (next.has(i)) next.delete(i); else next.add(i);
                  return next;
                })}
                onToggleFee={i => setCancelBillableFees(prev => {
                  const next = new Set(prev);
                  if (next.has(i)) next.delete(i); else next.add(i);
                  return next;
                })}
              />
              {(cancelBillableSectors.size + cancelBillableFees.size) > 0 && (
                <p className="text-xs text-gray-700 mt-2">
                  {cancelBillableSectors.size + cancelBillableFees.size} item
                  {(cancelBillableSectors.size + cancelBillableFees.size) !== 1 ? 's' : ''} will be flagged for invoicing.
                </p>
              )}
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={async () => {
                await act('cancelled', {
                  cancellationReason: cancelReason.trim(),
                  ...(isSTX && cancelBillableSectors.size > 0
                    ? { billableSectorIndices: [...cancelBillableSectors].sort((a, b) => a - b) }
                    : {}),
                  ...(isSTX && cancelBillableFees.size > 0
                    ? { billableFeeIndices: [...cancelBillableFees].sort((a, b) => a - b) }
                    : {}),
                });
                setShowCancelModal(false);
              }}
              disabled={!cancelReason.trim() || acting}
              className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {acting ? 'Cancelling…' : 'Confirm cancellation'}
            </button>
            <button
              onClick={() => setShowCancelModal(false)}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
            >
              Keep trip
            </button>
          </div>
        </div>
      )}

      {/* Trip header card */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{trip.title || '—'}</h2>
            {trip.declineReason && trip.status === 'declined' && (
              <p className="text-sm text-red-600 mt-1">Declined: {trip.declineReason}</p>
            )}
            {trip.cancellationReason && trip.status === 'cancelled' && (
              <p className="text-sm text-gray-700 mt-1">Cancelled: {trip.cancellationReason}</p>
            )}
          </div>
          <StatusBadge status={getDisplayStatus(trip)} />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 sm:gap-x-6 gap-y-3 text-sm">
          {trip.tripRef && (
            <div>
              <p className="text-xs text-gray-600 mb-0.5">Reference</p>
              <p className="text-gray-800 font-mono text-sm">{trip.tripRef}</p>
            </div>
          )}
          <div>
            <p className="text-xs text-gray-600 mb-0.5">Traveller</p>
            <p className="text-gray-800">{fmt(trip.travellerName)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-600 mb-0.5">Trip type</p>
            <p className="text-gray-800">{fmt(trip.tripType)}</p>
          </div>
          {/* Only set when the traveller sits outside the reporting line and the trip named its
              own approver — otherwise approval follows the usual scope rules and there is nothing
              trip-specific to show. */}
          {trip.nominatedApproverName && (
            <div>
              <p className="text-xs text-gray-600 mb-0.5">Nominated approver</p>
              <p className="text-gray-800">{trip.nominatedApproverName}</p>
            </div>
          )}
          <div>
            <p className="text-xs text-gray-600 mb-0.5">Cost centre</p>
            {showCCEdit ? (
              <div className="space-y-2 mt-1 col-span-2">
                <select
                  className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-gray-500"
                  value={newCC}
                  onChange={e => setNewCC(e.target.value)}
                >
                  <option value="">Not set</option>
                  {costCentres.map(c => <option key={c}>{c}</option>)}
                </select>
                <textarea
                  rows={2}
                  className="w-full border border-amber-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="Reason for change (required)…"
                  value={ccReason}
                  onChange={e => setCCReason(e.target.value)}
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleCostCentreChange}
                    disabled={ccSaving || !ccReason.trim()}
                    className="px-3 py-1 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {ccSaving ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    onClick={() => { setShowCCEdit(false); setCCReason(''); }}
                    className="px-3 py-1 text-xs text-gray-700 hover:text-gray-900"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <p className="text-gray-800">
                  {trip.costCentre || <span className="text-gray-600 italic">Not set</span>}
                </p>
                {canEditCostCentre && costCentres.length > 0 && trip.status !== 'cancelled' && (
                  <button
                    onClick={() => { setNewCC(trip.costCentre || ''); setShowCCEdit(true); }}
                    className="p-0.5 text-gray-600 hover:text-blue-600 rounded"
                    aria-label="Change cost centre"
                  >
                    <Edit2 size={11} />
                  </button>
                )}
              </div>
            )}
          </div>
          {(trip.originCity || trip.destinationCity) && (
            <div>
              <p className="text-xs text-gray-600 mb-0.5">Route</p>
              <p className="text-gray-800">
                {trip.originCity && trip.destinationCity
                  ? `${trip.originCity} → ${trip.destinationCity}`
                  : trip.originCity || trip.destinationCity}
              </p>
            </div>
          )}
          {trip.startDate && (
            <div>
              <p className="text-xs text-gray-600 mb-0.5">Dates</p>
              <p className="text-gray-800">
                {trip.startDate}
                {trip.endDate && trip.endDate !== trip.startDate ? ` → ${trip.endDate}` : ''}
              </p>
            </div>
          )}
          {(() => {
            const days = leadTimeDays(trip);
            if (days === null) return null;
            return (
              <div>
                <p className="text-xs text-gray-600 mb-1">Lead time</p>
                <LeadTimeBadge days={days} />
                <p className="text-xs text-gray-600 mt-1">{days} day{days !== 1 ? 's' : ''} before travel</p>
              </div>
            );
          })()}
          {totalCost > 0 && (
            <div>
              <p className="text-xs text-gray-600 mb-0.5">
                {creditInclGST > 0 ? 'Client will be billed (incl. GST)' : 'Estimated total (incl. GST)'}
              </p>
              <p className="text-gray-800 font-medium">A${netTotalInclGST.toFixed(2)}</p>
              <p className="text-xs text-gray-600 mt-0.5">A${netTotalExGST.toFixed(2)} ex-GST</p>
              {creditInclGST > 0 && (
                <p className="text-xs text-emerald-800 mt-1">
                  A${totalCost.toFixed(2)} less A${creditInclGST.toFixed(2)} travel credit
                </p>
              )}
            </div>
          )}
        </div>

        {trip.additionalPassengers?.length > 0 && (() => {
          const numPax = 1 + trip.additionalPassengers.length;
          const primaryCost = trip.primaryAllocatedCost != null
            ? trip.primaryAllocatedCost
            : (totalCost / numPax);
          return (
            <div className="mt-4 pt-4 border-t border-gray-100 col-span-3">
              <p className="text-xs text-gray-600 mb-2">All passengers</p>
              <div className="space-y-2">
                {/* Primary traveller */}
                <div className="flex items-center gap-3 text-sm p-2 bg-blue-50 rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{trip.travellerName}</p>
                    <p className="text-xs text-gray-700 mt-0.5">
                      Primary traveller{trip.costCentre ? ` · ${trip.costCentre}` : ''}
                    </p>
                  </div>
                  <span className="text-xs font-medium text-gray-700 shrink-0">A${primaryCost.toFixed(2)}</span>
                </div>
                {/* Additional passengers */}
                {trip.additionalPassengers.map((p, i) => {
                  const sectorLabels = (p.sectorIndices || [])
                    .map(idx => { const s = trip.sectors?.[idx]; return s ? (SECTOR_LABELS[s.type] || s.type) : null; })
                    .filter(Boolean);
                  return (
                    <div key={i} className="flex items-center gap-3 text-sm p-2 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 flex items-center gap-1.5 flex-wrap">
                          {p.name}
                          {['support_worker', 'carer'].includes(p.role) && (
                            <span className="inline-flex px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">Support worker</span>
                          )}
                        </p>
                        <p className="text-xs text-gray-700 mt-0.5">
                          {p.costCentre && <span>{p.costCentre} · </span>}
                          {sectorLabels.length > 0 ? sectorLabels.join(', ') : 'All sectors'}
                        </p>
                      </div>
                      <span className="text-xs font-medium text-gray-700 shrink-0">
                        A${(p.allocatedCost ?? 0).toFixed(2)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {trip.purpose && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs text-gray-600 mb-1">Purpose / notes</p>
            <p className="text-sm text-gray-700 whitespace-pre-line">{trip.purpose}</p>
          </div>
        )}

        {isSTX && trip.internalNotes && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs text-gray-600 mb-1 flex items-center gap-1">
              <Lock size={11} /> STX internal notes
            </p>
            <p className="text-sm text-gray-700 whitespace-pre-line">{trip.internalNotes}</p>
          </div>
        )}

        {isSTX && trip.status === 'cancelled' && (cancelCandidates.sectors.length > 0 || cancelCandidates.fees.length > 0) && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-start justify-between gap-3 mb-1">
              <p className="text-xs text-gray-600 flex items-center gap-1">
                <Receipt size={11} aria-hidden="true" /> Non-refundable items flagged for invoicing
              </p>
              {billableEdit === null && (
                <button
                  onClick={() => setBillableEdit({
                    sectors: new Set(trip.billableSectorIndices || []),
                    fees:    new Set(trip.billableFeeIndices    || []),
                  })}
                  className="text-xs text-blue-600 hover:text-blue-800 shrink-0"
                >
                  Edit
                </button>
              )}
            </div>

            {billableEdit === null ? (
              (trip.billableSectorIndices?.length > 0 || trip.billableFeeIndices?.length > 0) ? (
                <ul className="space-y-0.5">
                  {(trip.billableSectorIndices || []).map(i => {
                    const s = trip.sectors?.[i];
                    if (!s) return null;
                    const label = SECTOR_LABELS[s.type] || s.type;
                    return (
                      <li key={`s${i}`} className="text-xs text-gray-700">
                        · {label}{parseFloat(s.cost) ? ` — A$${parseFloat(s.cost).toFixed(2)}` : ''}
                      </li>
                    );
                  }).filter(Boolean)}
                  {(trip.billableFeeIndices || []).map(i => {
                    const f = trip.fees?.[i];
                    if (!f) return null;
                    const incGST = parseFloat(((parseFloat(f.amount) || 0) * (1 + (f.gstRate ?? 0.1))).toFixed(2));
                    return (
                      <li key={`f${i}`} className="text-xs text-gray-700">
                        · {feeLabelOf(f)} — A${incGST.toFixed(2)} incl. GST
                      </li>
                    );
                  }).filter(Boolean)}
                </ul>
              ) : (
                <p className="text-xs text-gray-700">
                  None flagged — this trip will not appear on an invoice.
                </p>
              )
            ) : (
              <div>
                <div className="mt-2">
                  <BillableItemsChecklist
                    trip={trip}
                    sectorSel={billableEdit.sectors}
                    feeSel={billableEdit.fees}
                    onToggleSector={i => setBillableEdit(prev => {
                      const next = new Set(prev.sectors);
                      if (next.has(i)) next.delete(i); else next.add(i);
                      return { ...prev, sectors: next };
                    })}
                    onToggleFee={i => setBillableEdit(prev => {
                      const next = new Set(prev.fees);
                      if (next.has(i)) next.delete(i); else next.add(i);
                      return { ...prev, fees: next };
                    })}
                  />
                </div>
                <p className="text-xs text-gray-700 mt-2">
                  Only ticked items are billed. Anything already invoiced stays on that invoice — this
                  only affects future invoice scans.
                </p>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={handleBillableSave}
                    disabled={billableSaving}
                    className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {billableSaving ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    onClick={() => setBillableEdit(null)}
                    className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {isSTX && trip.vtoTripId && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs text-gray-600 mb-1 flex items-center gap-1">
              <Lock size={11} /> VTO Trip ID
            </p>
            <p className="text-sm text-gray-700 font-mono">{trip.vtoTripId}</p>
          </div>
        )}

        {trip.tripType !== 'Self-Managed' && trip.digitalItineraryLink && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs text-gray-600 mb-1">Digital Itinerary</p>
            <a
              href={/^https?:\/\//i.test(trip.digitalItineraryLink) ? trip.digitalItineraryLink : `https://${trip.digitalItineraryLink}`}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-blue-600 hover:text-blue-800 underline break-all"
            >
              {trip.digitalItineraryLink}
            </a>
          </div>
        )}
      </div>

      {/* C5: Travel insurance */}
      {trip.travelInsurance && (trip.travelInsurance.provider || trip.travelInsurance.policyNumber || trip.travelInsurance.emergencyPhone) && (
        <div className="bg-white rounded-xl border border-teal-200 p-5 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Shield size={14} aria-hidden="true" className="text-teal-600 shrink-0" />
            <h3 className="text-sm font-semibold text-gray-700">Travel Insurance</h3>
          </div>
          {trip.travelInsurance.emergencyPhone && ['approved', 'booked', 'travelling'].includes(getDisplayStatus(trip)) && (
            <div className="mb-4 p-3 bg-teal-50 border border-teal-200 rounded-lg">
              <p className="text-xs text-gray-600 mb-0.5">24h Emergency phone</p>
              <p className="text-2xl font-bold text-teal-700 tracking-wide">{trip.travelInsurance.emergencyPhone}</p>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
            <Row label="Provider" value={trip.travelInsurance.provider} />
            <Row label="Policy no." value={trip.travelInsurance.policyNumber} />
            <Row label="Cover from" value={trip.travelInsurance.coverFrom} />
            <Row label="Cover to" value={trip.travelInsurance.coverTo} />
            {!['approved', 'booked', 'travelling'].includes(getDisplayStatus(trip)) && trip.travelInsurance.emergencyPhone && (
              <Row label="Emergency phone" value={trip.travelInsurance.emergencyPhone} />
            )}
            {trip.travelInsurance.notes && (
              <div className="col-span-2 flex gap-2 pt-1">
                <span className="text-xs text-gray-600 w-32 shrink-0">Notes</span>
                <span className="text-xs text-gray-700">{trip.travelInsurance.notes}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {travellerPassenger && (
        <div className="mb-4">
          <TravellerAccessibilityCard passenger={travellerPassenger} />
        </div>
      )}

      {/* A6: Pre-booking checklist — STX only, approved trips */}
      {isSTX && trip.status === 'approved' && (
        <div className="mb-4 bg-white rounded-xl border border-gray-200 overflow-hidden">
          <button
            type="button"
            onClick={() => {
              if (!checklistOpen && localChecklist === null) {
                setLocalChecklist(trip.bookingChecklist || generateChecklist(trip, travellerPassenger));
              }
              setChecklistOpen(v => !v);
            }}
            className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
            aria-expanded={checklistOpen}
          >
            <CheckSquare size={14} aria-hidden="true" className="text-indigo-600 shrink-0" />
            <span className="text-sm font-medium text-gray-700 flex-1">Pre-booking checklist</span>
            {(() => {
              const items = localChecklist || trip.bookingChecklist;
              if (!items) return null;
              const done = items.filter(i => i.checked || i.waived).length;
              const total = items.length;
              return (
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${done === total ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                  {done}/{total} complete
                </span>
              );
            })()}
            {checklistOpen
              ? <ChevronUp size={14} aria-hidden="true" className="text-gray-500 shrink-0" />
              : <ChevronDown size={14} aria-hidden="true" className="text-gray-500 shrink-0" />
            }
          </button>
          {checklistOpen && localChecklist && (
            <div className="px-4 pb-4">
              <div className="space-y-2 mb-3 mt-1">
                {localChecklist.map((item, idx) => (
                  <div key={item.key} className={`py-2 border-b border-gray-100 last:border-0 ${item.waived ? 'opacity-60' : ''}`}>
                    <div className="flex items-start gap-2.5">
                      <input
                        type="checkbox"
                        id={`cl-${item.key}`}
                        checked={item.checked}
                        disabled={item.waived}
                        onChange={e => setLocalChecklist(prev => prev.map((it, i) => i === idx ? { ...it, checked: e.target.checked } : it))}
                        className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <label htmlFor={`cl-${item.key}`} className={`text-sm flex-1 cursor-pointer ${item.checked ? 'line-through text-gray-500' : 'text-gray-700'}`}>
                        {item.label}
                      </label>
                      {!item.checked && (
                        item.waived ? (
                          <button
                            type="button"
                            onClick={() => setLocalChecklist(prev => prev.map((it, i) => i === idx ? { ...it, waived: false, waiveReason: '' } : it))}
                            className="text-xs text-blue-600 hover:text-blue-800 shrink-0"
                          >
                            Undo
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setLocalChecklist(prev => prev.map((it, i) => i === idx ? { ...it, waived: true, waiveReason: '' } : it))}
                            className="text-xs text-gray-600 hover:text-gray-800 shrink-0"
                          >
                            N/A
                          </button>
                        )
                      )}
                    </div>
                    {item.waived && (
                      <input
                        className="mt-1 ml-6 text-xs border border-gray-200 rounded px-2 py-1 w-64 focus:outline-none focus:ring-1 focus:ring-indigo-400 placeholder:text-gray-500"
                        placeholder="Reason not applicable (optional)"
                        value={item.waiveReason || ''}
                        onChange={e => setLocalChecklist(prev => prev.map((it, i) => i === idx ? { ...it, waiveReason: e.target.value } : it))}
                      />
                    )}
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={async () => {
                    setChecklistSaving(true);
                    try { await onUpdate({ bookingChecklist: localChecklist }); } finally { setChecklistSaving(false); }
                  }}
                  disabled={checklistSaving}
                  className="px-3 py-1.5 bg-indigo-600 text-white text-xs rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {checklistSaving ? 'Saving…' : 'Save checklist'}
                </button>
                {localChecklist.some(i => !i.checked && !i.waived) && (
                  <p className="text-xs text-amber-600">
                    {localChecklist.filter(i => !i.checked && !i.waived).length} item{localChecklist.filter(i => !i.checked && !i.waived).length !== 1 ? 's' : ''} remaining
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Sectors */}
      {(trip.sectors || []).length > 0 && (
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            Sectors ({trip.sectors.length})
          </h3>

          {/* Nuitee outcome banner — saved option or confirmed booking */}
          {nuiteeBookedSector && (
            nuiteeBookedSector.isOption ? (
              <div className="mb-3 p-3 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-2 text-sm text-amber-900">
                <Hotel size={15} aria-hidden="true" className="text-amber-700 shrink-0 mt-0.5" />
                <span>
                  <strong>Hotel option saved{nuiteeBookedSector.propertyName ? ` — ${nuiteeBookedSector.propertyName}` : ''}.</strong>{' '}
                  Nothing has been booked. The hotel, dates and price are now on this sector for the
                  approver to review. You can book it once the trip is approved.
                </span>
              </div>
            ) : (
              <div className="mb-3 p-3 bg-teal-50 border border-teal-300 rounded-lg flex items-center gap-2 text-sm text-teal-800">
                <CheckCircle size={15} aria-hidden="true" className="text-teal-600 shrink-0" />
                <span>
                  <strong>Hotel booked via Nuitee.</strong>{' '}
                  {nuiteeBookedSector.propertyName
                    ? <><strong>{nuiteeBookedSector.propertyName}</strong> has been saved to this sector.</>
                    : 'Your booking details have been saved to this sector.'}
                  {' '}Mark as booked once you are ready to confirm.
                </span>
              </div>
            )
          )}

          <div className="space-y-3">
            {trip.sectors.map((s, i) => (
              <SectorCard
                key={i}
                sector={s}
                index={i}
                onSearchHotels={
                  canSearchHotels && s.type === 'accommodation'
                    ? () => openHotelBookingFromDetail(i)
                    : undefined
                }
                hotelMode={hotelMode}
                onSearchFlights={
                  // Not on a return leg: it belongs to the outbound's booking, and searching
                  // from it would create a second, unrelated booking for the same journey.
                  canSearchFlights && s.type === 'flight' && !s.returnLegOf
                    ? () => openFlightBookingFromDetail(i)
                    : undefined
                }
                flightMode={flightMode}
                flightBookingLocked={flightBookingLocked}
                flightLockReason={flightLockReason}
                canRecordSsr={canEdit || isSTX}
                onRecordSsr={(i) => { setSsrSector(i); setSsrRef(''); }}
                onCancelFlight={isSTX ? (i) => setCancelSector(i) : undefined}
              />
            ))}
          </div>
        </div>
      )}

      {/* B6: Record actuals — completed trips, STX + client_ops */}
      {getDisplayStatus(trip) === 'completed' && (isSTX || role === 'client_ops') && (trip.sectors || []).some(s => parseFloat(s.cost) > 0) && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
              <Receipt size={13} className="text-gray-600" />
              Actual costs
              {trip.actualsRecorded && (
                <span className="ml-2 text-xs font-medium px-2 py-0.5 bg-green-100 text-green-700 rounded-full">Recorded</span>
              )}
            </h3>
            {!actualsEditing && (
              <button
                type="button"
                onClick={() => {
                  const init = {};
                  (trip.sectors || []).forEach((s, i) => {
                    init[i] = s.actualCost != null ? String(s.actualCost) : (s.cost || '');
                  });
                  setActualCosts(init);
                  setActualsEditing(true);
                }}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                {trip.actualsRecorded ? 'Update actuals' : 'Record actuals'}
              </button>
            )}
          </div>
          {actualsEditing ? (
            <div>
              <div className="space-y-2 mb-3">
                {(trip.sectors || []).map((s, i) => {
                  if (!(parseFloat(s.cost) > 0)) return null;
                  const Icon = SECTOR_ICONS[s.type] || MoreHorizontal;
                  const label = SECTOR_LABELS[s.type] || s.type;
                  const summary = s.type === 'flight' ? [s.from, s.to].filter(Boolean).join(' → ')
                    : s.type === 'accommodation' ? s.propertyName
                    : s.type === 'car-hire' ? s.company
                    : '';
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <Icon size={13} aria-hidden="true" className="text-gray-600 shrink-0" />
                      <span className="text-xs text-gray-700 flex-1 min-w-0 truncate">
                        {label}{summary ? ` — ${summary}` : ''}
                      </span>
                      <span className="text-xs text-gray-600 w-24 text-right shrink-0">Est. A${(parseFloat(s.cost) || 0).toFixed(2)}</span>
                      <div className="flex items-center gap-1 shrink-0">
                        <span className="text-xs text-gray-600">Actual A$</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={actualCosts[i] ?? ''}
                          onChange={e => setActualCosts(prev => ({ ...prev, [i]: e.target.value }))}
                          className="w-24 border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder:text-gray-500"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSaveActuals}
                  disabled={actualsSubmitting}
                  className="px-3 py-1.5 bg-teal-600 text-white text-xs rounded-lg hover:bg-teal-700 disabled:opacity-50"
                >
                  {actualsSubmitting ? 'Saving…' : 'Save actuals'}
                </button>
                <button
                  type="button"
                  onClick={() => setActualsEditing(false)}
                  className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : trip.actualsRecorded ? (
            <div className="space-y-1.5">
              {(trip.sectors || []).map((s, i) => {
                if (s.actualCost == null) return null;
                const Icon = SECTOR_ICONS[s.type] || MoreHorizontal;
                const label = SECTOR_LABELS[s.type] || s.type;
                const est = parseFloat(s.cost) || 0;
                const actual = parseFloat(s.actualCost) || 0;
                const diff = actual - est;
                return (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <Icon size={12} aria-hidden="true" className="text-gray-600 shrink-0" />
                    <span className="text-gray-700 flex-1">{label}</span>
                    <span className="text-gray-600">Est. A${est.toFixed(2)}</span>
                    <span className="text-gray-700 font-medium">Actual A${actual.toFixed(2)}</span>
                    {Math.abs(diff) >= 0.01 && (
                      <span className={diff > 0 ? 'text-red-600 font-medium' : 'text-green-600 font-medium'}>
                        {diff > 0 ? '+' : ''}{diff.toFixed(2)}
                      </span>
                    )}
                  </div>
                );
              }).filter(Boolean)}
            </div>
          ) : (
            <p className="text-xs text-gray-600">No actual costs recorded. Click &ldquo;Record actuals&rdquo; to add them.</p>
          )}
        </div>
      )}

      {/* Fees */}
      {(trip.fees || []).length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5 mb-3">
            <Receipt size={13} className="text-gray-600" />
            Fees
          </h3>
          <div className="space-y-2">
            {trip.fees.map((fee, i) => {
              const incGST = parseFloat((fee.amount * (1 + (fee.gstRate || 0.1))).toFixed(2));
              const label = fee.type === 'management' ? 'Management Fee' : fee.type === 'amendment' ? 'Amendment Fee' : fee.label || 'Fee';
              return (
                // The remove button sits directly beside the fee text rather than flexing out to
                // the far edge of the card, where it read as unrelated to the line it acts on.
                <div key={i} className="flex items-center gap-2 px-3 py-2.5 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="min-w-0">
                    <p className="text-sm text-gray-800 font-medium">{fee.label || label}</p>
                    <p className="text-xs text-gray-600">
                      A${fee.amount?.toFixed(2)} ex-GST · A${incGST.toFixed(2)} inc. GST
                      {fee.appliedByName && ` · ${fee.appliedByName}`}
                      {fee.appliedAt && ` · ${new Date(fee.appliedAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit' })}`}
                    </p>
                  </div>
                  {isSTX && (
                    <button
                      type="button"
                      onClick={() => handleDeleteFee(fee)}
                      aria-label={`Remove fee: ${fee.label || label}`}
                      title={`Remove ${fee.label || label}`}
                      className="p-1.5 shrink-0 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    >
                      <Trash2 size={14} aria-hidden="true" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Attachments */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
        <Attachments
          trip={trip}
          clientId={clientId}
          onUpdate={onUpdate}
          canEdit={canEdit && trip.status !== 'cancelled'}
        />
      </div>

      {/* Amendment history */}
      {(trip.amendments || []).length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5 mb-3">
            <Clock size={13} className="text-gray-600" />
            History
          </h3>
          <div className="space-y-3">
            {[...(trip.amendments || [])].reverse().map((a, i) => (
              <div key={i} className="flex gap-3 text-xs">
                <span className="text-gray-600 shrink-0 w-20 pt-0.5">
                  {a.at ? new Date(a.at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit' }) : '—'}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-gray-600 leading-relaxed">
                    {a.type === 'status_change' ? (
                      <>Status changed to <strong>{fmtStatus(a.to)}</strong>{a.note ? ` — ${a.note}` : ''}</>
                    ) : (
                      a.note || 'Updated'
                    )}
                    {a.byName && <span className="text-gray-600"> · {a.byName}</span>}
                  </p>
                  {(a.changes || []).length > 0 && (
                    <ul className="mt-1.5 space-y-1 text-gray-700">
                      {a.changes.map((c, j) => {
                        const label = typeof c === 'string' ? c
                          : c?.field ? `${c.field}: "${c.from}" → "${c.to}"` : JSON.stringify(c);
                        return (
                        <li key={j} className="flex gap-1.5">
                          <span className="text-gray-500 shrink-0 select-none" aria-hidden="true">·</span>
                          <span>{label}</span>
                        </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Meta */}
      <div className="text-xs text-gray-600 space-y-0.5">
        {trip.createdAt?.toDate && (
          <p>Created {trip.createdAt.toDate().toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
        )}
        {isSTX && trip.clientId && <p>Client: {trip.clientId}</p>}
      </div>

      {showRatingModal && (
        <TripRatingModal
          trip={trip}
          onClose={submitted => {
            setShowRatingModal(false);
            if (submitted) {
              // Reload the existing rating
              const docId = `${trip.id}_${userProfile.uid}`;
              getDoc(doc(db, 'tripFeedback', docId))
                .then(snap => setExistingRating(snap.exists() ? snap.data() : null))
                .catch(() => {});
            }
          }}
          existingRating={existingRating}
        />
      )}

      {cancelSector !== null && (() => {
        const sec = (trip.sectors || [])[cancelSector] || {};
        return (
          <FlightCancelModal
            bookingId={sec.bookedFlight?.bookingId}
            bookingRef={sec.bookingRef}
            flightLabel={[sec.airline, sec.flightNumber, `${sec.from} → ${sec.to}`, sec.date]
              .filter(Boolean).join(' · ')}
            onClose={() => setCancelSector(null)}
            onCancelled={(result) => recordFlightCancelled(cancelSector, result)}
          />
        );
      })()}

      {/* Recording the airline's confirmation is what clears the outstanding state — there is no
          automatic signal, because the booking never carried the request in the first place. */}
      {ssrSector !== null && (
        <Modal title="Record airline confirmation" onClose={() => setSsrSector(null)}>
          <div className="space-y-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-900">
                Enter the reference the airline gave when you arranged the assistance — a case
                number, SSR confirmation, or the agent's reference. This is the only record that it
                was actually done.
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="ssr-ref">
                Airline confirmation reference *
              </label>
              <input
                id="ssr-ref"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-gray-500"
                value={ssrRef}
                onChange={e => setSsrRef(e.target.value)}
                placeholder="e.g. QF SSR case 884213, confirmed with Jo on 16 Sep"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setSsrSector(null)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
                Cancel
              </button>
              <button
                onClick={saveSsrConfirmation}
                disabled={ssrSaving || !ssrRef.trim()}
                className="px-5 py-2 bg-green-700 text-white text-sm rounded-lg hover:bg-green-800 disabled:opacity-50"
              >
                {ssrSaving ? 'Saving…' : 'Mark as arranged'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
